import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * useVoice — Speech-to-text hook
 *
 * Primary:  Web Speech API (SpeechRecognition) — built-in, zero latency, no key needed
 * Fallback: None needed — Web Speech API is supported in Chrome, Edge, Safari (iOS 14.5+)
 *
 * TTS (voice output) is handled separately via ElevenLabs in useChat.js
 */
export function useVoice() {
  const [isListening, setIsListening] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [partialTranscript, setPartialTranscript] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [volume, setVolume] = useState(0);
  const [vadScore, setVadScore] = useState(0);

  const recognitionRef = useRef(null);
  const committedRef = useRef('');
  const isMutedRef = useRef(false);
  const shouldKeepListeningRef = useRef(false);
  const speakTimerRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const sourceRef = useRef(null);

  useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);

  // Volume visualizer (mic level animation)
  const updateVolume = useCallback(() => {
    if (!analyserRef.current) return;
    const data = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) sum += data[i];
    const avg = sum / data.length / 255;
    setVolume(avg);
    setVadScore(avg);
    rafRef.current = requestAnimationFrame(updateVolume);
  }, []);

  const startVolumeAnalyser = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      streamRef.current = stream;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      sourceRef.current = source;
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      rafRef.current = requestAnimationFrame(updateVolume);
    } catch (e) {
      console.warn('Volume analyser failed (mic permission?):', e.message);
    }
  }, [updateVolume]);

  const stopVolumeAnalyser = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (sourceRef.current) { sourceRef.current.disconnect(); sourceRef.current = null; }
    if (analyserRef.current) { analyserRef.current = null; }
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setVolume(0);
    setVadScore(0);
  }, []);

  const cleanup = useCallback(() => {
    shouldKeepListeningRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      } catch (e) {}
      recognitionRef.current = null;
    }
    if (speakTimerRef.current) { clearTimeout(speakTimerRef.current); }
    stopVolumeAnalyser();
  }, [stopVolumeAnalyser]);

  const startListening = useCallback(async () => {
    if (isListening) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.error('Web Speech API not supported in this browser');
      return;
    }

    // Reset state
    setTranscript('');
    setPartialTranscript('');
    committedRef.current = '';
    setIsListening(true);
    setIsSpeaking(false);
    shouldKeepListeningRef.current = true;

    // Start volume visualizer (separate from STT)
    await startVolumeAnalyser();

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      if (isMutedRef.current) return;

      let finalText = committedRef.current;
      let interimText = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = (result[0]?.transcript || '').trim();
        if (!text) continue;
        if (result.isFinal) {
          finalText = (finalText + ' ' + text).trim();
        } else {
          interimText = text;
        }
      }

      committedRef.current = finalText;
      setTranscript(finalText);
      setPartialTranscript(interimText);

      // Update speaking state
      const hasSpeech = Boolean(interimText || (finalText !== committedRef.current));
      setIsSpeaking(true);
      setVadScore(0.8);

      if (speakTimerRef.current) clearTimeout(speakTimerRef.current);
      speakTimerRef.current = setTimeout(() => {
        setIsSpeaking(false);
        setVadScore(0.2);
        setPartialTranscript('');
      }, 1200);
    };

    recognition.onspeechstart = () => {
      setIsSpeaking(true);
      setVadScore(0.7);
    };

    recognition.onspeechend = () => {
      if (speakTimerRef.current) clearTimeout(speakTimerRef.current);
      speakTimerRef.current = setTimeout(() => {
        setIsSpeaking(false);
        setVadScore(0.1);
        setPartialTranscript('');
      }, 800);
    };

    recognition.onerror = (event) => {
      // 'no-speech' and 'aborted' are normal — don't log as errors
      if (!['no-speech', 'aborted'].includes(event.error)) {
        console.warn('SpeechRecognition error:', event.error);
      }
    };

    recognition.onend = () => {
      // Auto-restart to keep listening (continuous mode sometimes stops)
      if (shouldKeepListeningRef.current) {
        try { recognition.start(); } catch (e) {}
      }
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
    } catch (err) {
      console.error('Failed to start SpeechRecognition:', err);
      setIsListening(false);
      stopVolumeAnalyser();
    }
  }, [isListening, startVolumeAnalyser, stopVolumeAnalyser]);

  const stopListening = useCallback(() => {
    cleanup();
    setIsListening(false);
    setIsSpeaking(false);
    setPartialTranscript('');
  }, [cleanup]);

  const toggleMute = useCallback(() => setIsMuted(m => !m), []);

  const clearTranscript = useCallback(() => {
    committedRef.current = '';
    setTranscript('');
    setPartialTranscript('');
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const fullTranscript = partialTranscript
    ? (committedRef.current + ' ' + partialTranscript).trim()
    : transcript;

  return {
    isListening, isMuted,
    transcript: fullTranscript,
    committedTranscript: transcript,
    partialTranscript,
    vadScore, isSpeaking, volume,
    startListening, stopListening, toggleMute, clearTranscript,
  };
}
